import React, { useEffect, useState } from 'react';
import { UserPlus, Pencil, KeyRound, Trash2, X, ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';
import { authAPI } from '../../services/api';
import { getUser, MODULES, MODULE_LABELS, SIDEBAR_PAGES, PLATFORM_MODULES } from '../../services/session';

const LEVELS = [
  { value: 'none', label: 'No access' },
  { value: 'view', label: 'View only' },
  { value: 'edit', label: 'View & edit' },
];
// a preset gives every area the same level; Settings (own password) stays allowed unless "No access"
const preset = (level) => ({ ...Object.fromEntries(MODULES.map(m => [m, level])), settings: level === 'none' ? 'none' : 'edit' });
const PRESETS = [
  { label: 'Read-only (view everything)', perms: preset('view') },
  { label: 'Full access (view & edit everything)', perms: preset('edit') },
  { label: 'No access (start blank)', perms: preset('none') },
];

function accessSummary(u) {
  if (u.role === 'admin') return 'Admin: full access + manage accounts';
  const p = u.permissions || {};
  const vals = MODULES.map(m => p[m] || 'none');
  if (vals.every(v => v === 'view')) return 'Read-only, everything';
  if (vals.every(v => v === 'edit')) return 'Full access (not admin)';
  if (vals.every(v => v === 'none')) return 'No access';
  const pageName = Object.fromEntries(SIDEBAR_PAGES.map(pg => [pg.key, pg.label]));
  return MODULES.filter(m => (p[m] || 'none') !== 'none')
    .map(m => `${pageName[m] || MODULE_LABELS[m].split(' (')[0]}: ${m === 'settings' ? 'own password' : p[m] === 'edit' ? 'edit' : 'view'}`).join(' · ');
}

function PermissionRow({ label, value, levels, onChange, indent }) {
  return (
    <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-3 pr-4 ${indent ? 'pl-10 bg-gray-50' : 'pl-4'}`}>
      <span className="text-sm text-gray-800">{label}</span>
      <div className="flex flex-wrap gap-1">
        {levels.map(l => (
          <button key={l.value} type="button" onClick={() => onChange(l.value)}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              value === l.value
                ? l.value === 'edit' ? 'bg-green-600 text-white border-green-600'
                  : l.value === 'view' ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-gray-700 text-white border-gray-700'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
            {l.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function UserModal({ existing, onClose, onSaved }) {
  const me = getUser();
  const [username, setUsername] = useState(existing?.username || '');
  const [email, setEmail] = useState(existing?.email || '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(existing?.role || 'user');
  const [isActive, setIsActive] = useState(existing ? existing.is_active : true);
  const [perms, setPerms] = useState(existing?.permissions && existing.role !== 'admin' ? { ...preset('none'), ...existing.permissions } : preset('view'));
  const [saving, setSaving] = useState(false);
  const [pagesOpen, setPagesOpen] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const isSelf = existing && existing.id === me?.id;

  const save = async () => {
    if (!existing && !username.trim()) return toast.error('Username is required');
    if (!existing && password.length < 8) return toast.error('Password must be at least 8 characters');
    if (existing && password && password.length < 8) return toast.error('New password must be at least 8 characters');
    setSaving(true);
    try {
      const payload = { email, role, is_active: isActive, permissions: perms, ...(existing && password ? { password } : {}) };
      if (existing) await authAPI.updateUser(existing.id, payload);
      else await authAPI.createUser({ ...payload, username: username.trim(), password });
      toast.success(existing ? 'Account updated' : 'Account created');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save account');
    } finally {
      setSaving(false);
    }
  };

  const field = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none';
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h3 className="text-lg font-bold text-gray-800">{existing ? `Edit ${existing.username}` : 'Create account'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input className={field} value={username} onChange={e => setUsername(e.target.value)} disabled={!!existing} autoComplete="off" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
              <input className={field} value={email} onChange={e => setEmail(e.target.value)} type="email" autoComplete="off" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{existing ? 'New password' : 'Password'}</label>
              <div className="relative">
                <input className={field + ' pr-10'} value={password} onChange={e => setPassword(e.target.value)} type={showPw ? 'text' : 'password'}
                       placeholder={existing ? 'Leave blank to keep the current one' : 'At least 8 characters'} autoComplete="new-password" />
                <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-2 top-2 text-gray-400 hover:text-gray-700">{showPw ? <EyeOff size={18} /> : <Eye size={18} />}</button>
              </div>
              {existing && (
                <p className="text-xs text-gray-500 mt-1">
                  Current password: {existing.password
                    ? <span className="font-mono text-gray-800">{showPw ? existing.password : '•'.repeat(Math.min(existing.password.length, 12))}</span>
                    : 'not known yet (shows after their next login or password change)'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account type</label>
              <select className={field} value={role} onChange={e => setRole(e.target.value)} disabled={isSelf}>
                <option value="user">User (access set below)</option>
                <option value="admin">Admin (full access + manage accounts)</option>
              </select>
            </div>
          </div>

          {existing && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} disabled={isSelf} />
              Account active (untick to block this user from logging in)
            </label>
          )}

          {role === 'user' ? (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <label className="text-sm font-medium text-gray-700">What this account can access</label>
                <select className="text-sm border border-gray-300 rounded-lg px-2 py-1" value="" onChange={e => { const p = PRESETS[Number(e.target.value)]; if (p) setPerms({ ...p.perms }); }}>
                  <option value="">Quick preset…</option>
                  {PRESETS.map((p, i) => <option key={i} value={i}>{p.label}</option>)}
                </select>
              </div>
              <div className="border rounded-xl divide-y">
                <button type="button" onClick={() => setPagesOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50">
                  <span className="text-sm text-gray-800 font-medium flex items-center gap-2">
                    {pagesOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />} Summary page (sidebar pages)
                  </span>
                  <span className="text-xs text-gray-500">{SIDEBAR_PAGES.filter(pg => (perms[pg.key] || 'none') !== 'none').length} of {SIDEBAR_PAGES.length} pages allowed</span>
                </button>
                {pagesOpen && SIDEBAR_PAGES.map(pg => (
                  <PermissionRow key={pg.key} label={pg.label} indent value={perms[pg.key] || 'none'}
                                 levels={pg.levels.map(([value, label]) => ({ value, label }))}
                                 onChange={v => setPerms(p => ({ ...p, [pg.key]: v }))} />
                ))}
                {PLATFORM_MODULES.map(m => (
                  <PermissionRow key={m} label={MODULE_LABELS[m]} value={perms[m] || 'none'} levels={LEVELS}
                                 onChange={v => setPerms(p => ({ ...p, [m]: v }))} />
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                "View only" shows the data but blocks changes (imports, pushes, syncs, edits). Settings only lets the account change its own password. Store credentials and the Users page are always admin-only.
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-600 bg-purple-50 border border-purple-100 rounded-lg px-4 py-3">
              Admins can see and change everything, including store credentials, and can create and manage other accounts.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-50">
            {saving ? 'Saving…' : existing ? 'Save changes' : 'Create account'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UserManagement() {
  const me = getUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | user
  const [shown, setShown] = useState({}); // which passwords are revealed

  const load = async () => {
    setLoading(true);
    try { setUsers((await authAPI.listUsers()).data.users || []); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not load accounts'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resetPassword = async (u) => {
    const pw = window.prompt(`New password for ${u.username} (at least 8 characters):`);
    if (pw === null) return;
    if (pw.length < 8) return toast.error('Password must be at least 8 characters');
    try { await authAPI.resetPassword(u.id, pw); toast.success(`Password reset for ${u.username}`); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not reset password'); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete the account "${u.username}"? This cannot be undone.`)) return;
    try { await authAPI.deleteUser(u.id); toast.success('Account deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not delete account'); }
  };

  return (
    <div className="max-w-6xl mx-auto w-full">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">Users</h1>
          <p className="text-gray-400">Create accounts and choose exactly what each one can see or change</p>
        </div>
        <button onClick={() => setEditing('new')} className="flex items-center gap-2 px-4 py-2.5 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-200">
          <UserPlus size={18} /> Create account
        </button>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 border-b border-white/10">
              <th className="px-4 py-3 font-medium">Username</th>
              <th className="px-4 py-3 font-medium">Password</th>
              <th className="px-4 py-3 font-medium">Access</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>
            ) : users.map(u => (
              <tr key={u.id} className="border-b border-white/5 last:border-0">
                <td className="px-4 py-3 text-white">
                  {u.username}{u.id === me?.id && <span className="ml-2 text-xs text-gray-400">(you)</span>}
                  {u.email && <div className="text-xs text-gray-500">{u.email}</div>}
                </td>
                <td className="px-4 py-3">
                  {u.password ? (
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-gray-200">{shown[u.id] ? u.password : '•'.repeat(Math.min(u.password.length, 12))}</span>
                      <button onClick={() => setShown(v => ({ ...v, [u.id]: !v[u.id] }))} title={shown[u.id] ? 'Hide' : 'Show'} className="text-gray-400 hover:text-white">
                        {shown[u.id] ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </span>
                  ) : <span className="text-xs text-gray-500" title="Shows after the next login or password change">not known yet</span>}
                </td>
                <td className="px-4 py-3 text-gray-300">{accessSummary(u)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${u.is_active ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-400'}`}>
                    {u.is_active ? 'Active' : 'Deactivated'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setEditing(u)} title="Edit access" className="p-2 rounded-lg text-gray-300 hover:bg-white/10"><Pencil size={16} /></button>
                    <button onClick={() => resetPassword(u)} title="Reset password" className="p-2 rounded-lg text-gray-300 hover:bg-white/10"><KeyRound size={16} /></button>
                    {u.id !== me?.id && (
                      <button onClick={() => remove(u)} title="Delete account" className="p-2 rounded-lg text-red-300 hover:bg-red-500/10"><Trash2 size={16} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <UserModal existing={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}
